import { ChartBarHorizontal, type Icon, Kanban, ListBullets, ListNumbers } from "@phosphor-icons/react";
import { m } from "@plannello/i18n/messages";
import type { ViewDto } from "@plannello/shared";
import { useSyncExternalStore } from "react";

/** Built-in views come from the server in English; their names are localized here by slug. */
export function viewName(view: Pick<ViewDto, "builtin" | "slug" | "name">): string {
  if (!view.builtin) return view.name;
  switch (view.slug) {
    case "all-open":
      return m.views_builtin_all_open();
    case "board":
      return m.views_builtin_board();
    case "next-up":
      return m.nextup_title();
    case "triage":
      return m.views_builtin_triage();
    case "recently-closed":
      return m.views_builtin_recently_closed();
    default:
      return view.name;
  }
}

export function viewIcon(view: Pick<ViewDto, "layout" | "queue" | "slug">): Icon {
  if (view.queue || view.slug === "next-up") return ListNumbers;
  if (view.layout === "board") return Kanban;
  if (view.layout === "timeline") return ChartBarHorizontal;
  return ListBullets;
}

export const isShared = (v: Pick<ViewDto, "scope" | "builtin">) => !v.builtin && v.scope !== "personal";

/* "Save view" dialog requests: the views menu, the dirty controls and the palette open the same host. */

export type SaveRequest = { mode: "create" } | { mode: "edit"; view: ViewDto };
type SaveState = { request: SaveRequest | null; seq: number };
let saveState: SaveState = { request: null, seq: 0 };
const listeners = new Set<() => void>();
const emit = () => {
  for (const l of listeners) l();
};

export function openSaveView(request: SaveRequest) {
  saveState = { request, seq: saveState.seq + 1 };
  emit();
}

export function closeSaveView() {
  saveState = { ...saveState, request: null };
  emit();
}

export function useSaveViewState(): SaveState {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => saveState,
  );
}

/* "Open the views menu" requests (V, palette): the menu of the current product page listens. */

let menuSeq = 0;
const menuListeners = new Set<() => void>();

export function openViewsMenu() {
  menuSeq++;
  for (const l of menuListeners) l();
}

export function useViewsMenuRequests(): number {
  return useSyncExternalStore(
    (l) => {
      menuListeners.add(l);
      return () => menuListeners.delete(l);
    },
    () => menuSeq,
  );
}
